/**
 * The Starter App Template's landing page — the day-one win.
 *
 * The student types their app's name, picks a colour, and this is what they see
 * running, with their name on it, about ten minutes into the course.
 *
 * It is styled as a small but polished landing page — a top bar, a hero
 * (headline + blurb + call-to-action + a placeholder image), and a "what's
 * inside" section that still showcases the three things the course promises to
 * teach: sign in (accounts), a saved list (the database), and an AI feature
 * (calling Claude). The copy is personalized from the idea they described in the
 * previous lesson (V2101) when it's available, and falls back to generic
 * template copy when it isn't.
 *
 * TWO CALLERS, TWO MODES
 *
 * `inline: true` (the default) returns ONE self-contained document with the CSS in
 * a <style> tag and inert buttons. That is what TemplateStudioLayout renders into
 * an <iframe srcDoc> for lesson CD108 / V2102, and it has to stay self-contained:
 * the frame is sandboxed WITHOUT `allow-same-origin`, so it has an opaque origin
 * where `localStorage` throws and no relative URL would resolve anyway. The preview
 * is cosmetic by definition, not by choice.
 *
 * `inline: false` returns the `index.html` of the downloadable project — same
 * markup, but the styling moves to `styles.css` and the calls to action become real
 * links to `app.html`. See projectTemplate.js for the rest of the project.
 *
 * Why an interpolated string rather than React components:
 *   - `srcDoc` isolates it completely from CodeNec's own Bootstrap/SCSS, so the
 *     preview looks like a separate app because it *is* one.
 *   - No build step, no network, no CSP surprises — it renders offline and on a
 *     phone. The imagery is drawn with inline SVG placeholders for the same
 *     reason: nothing to fetch.
 *   - It is the canonical brand surface: the same four tokens are what the real
 *     project reads from `config.js`, so the name and colours they choose today
 *     carry into the real codebase later.
 *
 * The screens are faked in-page on purpose — this lesson is explicitly cosmetic.
 * The real construction is Module 4.
 */

/** Accent colours offered in the Studio. Kept here so the swatches and the
 *  rendered app can never drift apart. */
export const ACCENT_COLORS = [
    "#6d5efc", // indigo (CodeNec's own accent)
    "#0ea5e9", // sky
    "#16a34a", // green
    "#d97706", // amber
    "#dc2626", // red
    "#db2777", // pink
];

export const DEFAULT_BRAND = {
    appName: "",
    tagline: "",
    accentColor: ACCENT_COLORS[0],
    emoji: "🚀",
};

/**
 * The localized chrome `buildStarterAppHtml` needs, gathered in one place.
 *
 * Two callers build the same document for different reasons — the Template Studio
 * previews it in an iframe (CD108 / V2102), and the build-spec lesson ships it as
 * the `index.html` of the downloadable project (V2401 / V3401) — and a label missing
 * from one of them renders as a raw i18n key inside the student's own app. Keeping
 * the list here means adding a label is a one-file change.
 *
 * @param {Function} t  i18next translate
 */
export const buildStarterAppLabels = (t) => ({
    untitled: t("templateStudio.preview.untitled"),
    defaultTagline: t("templateStudio.preview.defaultTagline"),
    ctaPrimary: t("templateStudio.preview.ctaPrimary"),
    heroTitle: t("templateStudio.preview.heroTitle"),
    heroBlurb: t("templateStudio.preview.heroBlurb"),
    builtFor: t("templateStudio.preview.builtFor"),
    insideTitle: t("templateStudio.preview.insideTitle"),
    signedInAs: t("templateStudio.preview.signedInAs"),
    demoUser: t("templateStudio.preview.demoUser"),
    featAccountsTitle: t("templateStudio.preview.featAccountsTitle"),
    featAccountsBody: t("templateStudio.preview.featAccountsBody"),
    welcomeBody: t("templateStudio.preview.welcomeBody"),
    itemsTitle: t("templateStudio.preview.itemsTitle"),
    itemsHint: t("templateStudio.preview.itemsHint"),
    item1: t("templateStudio.preview.item1"),
    item2: t("templateStudio.preview.item2"),
    item3: t("templateStudio.preview.item3"),
    aiTitle: t("templateStudio.preview.aiTitle"),
    aiHint: t("templateStudio.preview.aiHint"),
    aiReply: t("templateStudio.preview.aiReply"),
    highlightLabel: t("templateStudio.preview.highlightLabel"),
    footer: t("templateStudio.preview.footer"),
});

/**
 * Escape a user-supplied value before it goes into the iframe document.
 *
 * The student types these fields themselves, so this is not a trust boundary so
 * much as a correctness one — an apostrophe in "Rosa's Tacos" would otherwise
 * break the markup. Escaping quotes and angle brackets keeps any input rendering
 * as text.
 */
export const esc = (value) =>
    String(value == null ? "" : value)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");

/**
 * Substitute a `{{TOKEN}}` in a label with a value.
 *
 * Uses a replacer function rather than a plain string: `String.replace` treats
 * `$&`, `` $` ``, `$'` and `$$` as special in a string replacement, so a value
 * containing any of them would render mangled.
 */
const fillToken = (label, token, value) => String(label).replace(token, () => value);
const withAppName = (label, appName) => fillToken(label, "{{APP_NAME}}", appName);

/** Grey image placeholder with a centered image-icon glyph — the "add your own
 *  photo here" look, drawn inline so there's nothing to fetch. */
const placeholder = (extraClass) =>
    `<div class="ph${extraClass ? " " + extraClass : ""}" aria-hidden="true"><svg class="ph-ico" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2.5"/><circle cx="8.5" cy="8.5" r="1.6"/><path d="M21 15l-5-5L5 21"/></svg></div>`;

/**
 * The landing page's stylesheet, with the student's accent baked into `:root`.
 *
 * ONE source of truth for the colour. The preview inlines this in a <style> tag;
 * the download writes it to `styles.css`. Nothing in JavaScript ever needs the hex
 * — every coloured thing on both pages reads `var(--accent)` — which is what keeps
 * "change your app's colour" a one-line edit in one file.
 *
 * `.app-*` rules are used only by the project's `app.html`; they cost the preview
 * a few hundred unused bytes and save the download a second stylesheet.
 */
export const starterAppCss = (accent) => `  :root { --accent: ${accent}; --ink: #16203a; --muted: #5b6172; --line: #e9ebf2; --panel: #f7f8fb; }
  * { box-sizing: border-box; }
  html { scroll-behavior: smooth; }
  body {
    margin: 0;
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
    background: #fff;
    color: var(--ink);
    -webkit-font-smoothing: antialiased;
    overflow-x: hidden;
  }
  .wrap { max-width: 900px; margin: 0 auto; padding: 0 20px; }
  a { text-decoration: none; }

  /* Top bar */
  .topbar {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; padding: 12px 20px; border-bottom: 1px solid var(--line);
    position: sticky; top: 0; background: rgba(255,255,255,0.92);
    backdrop-filter: saturate(180%) blur(6px); z-index: 10;
  }
  .brand { display: flex; align-items: center; gap: 9px; min-width: 0; }
  .brand .logo {
    width: 30px; height: 30px; border-radius: 8px; flex: 0 0 auto;
    display: flex; align-items: center; justify-content: center;
    font-size: 17px; line-height: 1; background: var(--accent);
  }
  .brand .name {
    font-weight: 800; font-size: 15px; color: var(--ink);
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .cta {
    display: inline-block; background: var(--accent); color: #fff;
    border: 0; border-radius: 999px; padding: 9px 18px;
    font-family: inherit; font-size: 13px; font-weight: 700;
    cursor: pointer; white-space: nowrap;
  }
  .topbar .cta { padding: 8px 15px; }
  .cta--lg { padding: 12px 24px; font-size: 14px; }
  .cta--ghost {
    background: transparent; color: var(--accent);
    box-shadow: inset 0 0 0 1.5px var(--accent);
  }

  /* Hero */
  .hero { padding: 44px 0 36px; }
  .hero-inner { display: flex; flex-wrap: wrap; align-items: center; gap: 30px; }
  .hero-text { flex: 1 1 320px; min-width: 0; }
  .hero-media { flex: 1 1 300px; min-width: 0; }
  .eyebrow {
    display: inline-block; text-transform: uppercase; letter-spacing: 0.08em;
    font-size: 11px; font-weight: 800; color: var(--accent); margin-bottom: 12px;
  }
  .hero h1 {
    margin: 0 0 14px; color: var(--ink); font-weight: 800;
    font-size: clamp(27px, 5.2vw, 42px); line-height: 1.08; letter-spacing: -0.01em;
  }
  .hero p.lead { margin: 0 0 18px; color: var(--muted); font-size: 15px; line-height: 1.6; max-width: 46ch; }
  .built-for {
    display: inline-block; margin: 0 0 20px; padding: 5px 12px;
    background: var(--panel); border: 1px solid var(--line); border-radius: 999px;
    font-size: 12.5px; color: var(--muted);
  }

  /* Image placeholders */
  .ph {
    background: #eef0f5; border: 1px solid var(--line); border-radius: 14px;
    display: flex; align-items: center; justify-content: center;
    color: #b6bccc; width: 100%; aspect-ratio: 4 / 3;
  }
  .ph-ico { width: 40px; height: 40px; }
  .ph-sq { aspect-ratio: 1 / 1; border-radius: 12px; }
  .ph-sq .ph-ico { width: 30px; height: 30px; }

  /* Features */
  .features { background: var(--panel); border-top: 1px solid var(--line); padding: 40px 0; }
  .features h2 { margin: 0 0 6px; font-size: 21px; color: var(--ink); }
  .features .sub { margin: 0 0 22px; color: var(--muted); font-size: 13.5px; line-height: 1.55; max-width: 60ch; }
  .grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 14px; }
  .fcard { background: #fff; border: 1px solid var(--line); border-radius: 14px; padding: 16px; }
  .fcard .tick {
    width: 26px; height: 26px; border-radius: 8px; margin-bottom: 10px;
    display: flex; align-items: center; justify-content: center;
    background: var(--panel); /* fallback for browsers without color-mix */
    background: color-mix(in srgb, var(--accent) 14%, #fff);
    color: var(--accent); font-weight: 900; font-size: 14px;
  }
  .fcard h3 { margin: 0 0 5px; font-size: 15px; color: var(--ink); }
  .fcard p { margin: 0; font-size: 13px; color: var(--muted); line-height: 1.5; }
  .who { display: flex; align-items: center; gap: 9px; margin-top: 11px; font-size: 12.5px; color: var(--muted); }
  .avatar {
    width: 26px; height: 26px; border-radius: 50%; flex: 0 0 auto;
    background: var(--accent); color: #fff; font-size: 12px; font-weight: 700;
    display: flex; align-items: center; justify-content: center;
  }
  ul.items { list-style: none; margin: 11px 0 0; padding: 0; }
  ul.items li {
    display: flex; align-items: center; gap: 9px; padding: 8px 0;
    border-bottom: 1px solid #f0f1f6; font-size: 13px; color: var(--ink);
  }
  ul.items li:last-child { border-bottom: 0; }
  .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex: 0 0 auto; }
  .ai-reply {
    margin-top: 11px; padding: 11px 13px; background: var(--panel);
    border-left: 3px solid var(--accent); border-radius: 6px;
    font-size: 12.5px; line-height: 1.5; color: var(--ink);
  }

  /* About-style highlight band */
  .band { padding: 44px 0; }
  .band-inner { display: flex; flex-wrap: wrap; align-items: center; gap: 30px; }
  .band-media { flex: 1 1 240px; display: flex; gap: 14px; }
  .band-media .ph-sq { max-width: 150px; }
  .ph-sq--offset { margin-top: 26px; }
  .band-text { flex: 1 1 280px; min-width: 0; }
  .band-text h3 { margin: 6px 0 18px; font-size: clamp(20px, 4vw, 28px); color: var(--ink); line-height: 1.15; }

  footer { padding: 22px 16px 30px; text-align: center; font-size: 11.5px; color: #9aa0b5; border-top: 1px solid var(--line); }

  @media (max-width: 560px) {
    .hero { padding: 30px 0 24px; }
    .features, .band { padding: 30px 0; }
    .hero-inner, .band-inner { gap: 22px; }
  }

  /* ---- app.html only (see projectTemplate.js) ---- */
  .app-main { max-width: 620px; margin: 0 auto; padding: 26px 20px 60px; }
  .app-card {
    background: #fff; border: 1px solid var(--line); border-radius: 14px;
    padding: 18px; margin-bottom: 16px;
  }
  .app-card h2 { margin: 0 0 4px; font-size: 17px; }
  .app-card .hint { margin: 0 0 14px; font-size: 13px; color: var(--muted); line-height: 1.5; }
  .app-row { display: flex; gap: 8px; flex-wrap: wrap; }
  .app-row input {
    flex: 1 1 200px; min-width: 0; padding: 10px 12px; font: inherit; font-size: 14px;
    border: 1px solid var(--line); border-radius: 9px; background: #fff; color: var(--ink);
  }
  .app-row input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
  .app-empty { padding: 18px 0; text-align: center; color: var(--muted); font-size: 13px; }
  ul.items li .grow { flex: 1 1 auto; min-width: 0; overflow-wrap: anywhere; }
  /* Used by the delete button on every list row AND by Sign out, so it is NOT
     scoped to the list — scoping it there left the sign-out button unstyled.
     (No backticks in this file's CSS: it is a template literal.) */
  button.link {
    background: none; border: 0; padding: 4px 6px; cursor: pointer;
    color: var(--muted); font: inherit; font-size: 12px; text-decoration: underline;
  }
  .app-mode {
    display: flex; align-items: center; gap: 8px; margin-bottom: 16px; padding: 9px 13px;
    background: var(--panel); border: 1px solid var(--line); border-radius: 10px;
    font-size: 12.5px; color: var(--muted); line-height: 1.45;
  }
  .app-mode .pip { width: 8px; height: 8px; border-radius: 50%; background: var(--accent); flex: 0 0 auto; }
  .app-err {
    margin-top: 11px; padding: 10px 12px; border-radius: 8px; font-size: 12.5px;
    background: #fff4f4; border: 1px solid #f5c9c9; color: #8d2323;
  }
  [hidden] { display: none !important; }`;

/**
 * Build the landing page for a set of brand values.
 *
 * @param {object} brand
 * @param {string} brand.appName
 * @param {string} brand.tagline
 * @param {string} brand.accentColor
 * @param {string} brand.emoji
 * @param {object} [brand.idea]  Optional { whatItDoes, whoItsFor, oneFeature } from V2101
 * @param {object} labels  Localized UI strings (see templateStudio.preview.* in common.json)
 * @param {object} [options]
 * @param {boolean} [options.inline=true]  true → self-contained document for the
 *        sandboxed preview. false → the project's index.html, linking styles.css
 *        and pointing its calls to action at app.html.
 * @param {string} [options.lang="es"]  The document's `lang` attribute.
 * @returns {string} A complete HTML document.
 */
export const buildStarterAppHtml = (brand, labels, options = {}) => {
    const { inline = true, lang = "es" } = options;

    const appName = esc(brand?.appName || labels.untitled);
    const accent = esc(brand?.accentColor || DEFAULT_BRAND.accentColor);
    const emoji = esc(brand?.emoji || DEFAULT_BRAND.emoji);

    // The user's own tagline becomes the hero headline when they set one; the
    // generic headline is only a fallback, so the two are tracked separately.
    const rawTagline = String(brand?.tagline || "").trim();
    const tagline = esc(rawTagline);

    // Personalization from the idea captured in the previous lesson. Any field
    // may be empty — each one degrades to generic template copy on its own.
    const idea = brand?.idea || {};
    const whatItDoes = esc(String(idea.whatItDoes || "").trim());
    const whoItsFor = esc(String(idea.whoItsFor || "").trim());
    const oneFeature = esc(String(idea.oneFeature || "").trim());

    // Take the avatar initial from the RAW label — after escaping, a name
    // starting with e.g. "&" would yield "&amp;" instead of the character.
    const demoInitial = esc(String(labels.demoUser || "").charAt(0).toUpperCase());

    const l = {
        eyebrow: esc(labels.defaultTagline),
        ctaPrimary: esc(labels.ctaPrimary),
        heroTitle: withAppName(esc(labels.heroTitle), appName),
        heroBlurb: esc(labels.heroBlurb),
        insideTitle: esc(labels.insideTitle),
        welcomeBody: esc(labels.welcomeBody),
        featAccountsTitle: esc(labels.featAccountsTitle),
        featAccountsBody: esc(labels.featAccountsBody),
        signedInAs: esc(labels.signedInAs),
        demoUser: esc(labels.demoUser),
        itemsTitle: esc(labels.itemsTitle),
        itemsHint: esc(labels.itemsHint),
        item1: esc(labels.item1),
        item2: esc(labels.item2),
        item3: esc(labels.item3),
        aiTitle: esc(labels.aiTitle),
        aiHint: esc(labels.aiHint),
        aiReply: withAppName(esc(labels.aiReply), appName),
        highlightLabel: esc(labels.highlightLabel),
        footer: esc(labels.footer),
    };

    // The preview's buttons go nowhere — there is nowhere for them to go inside a
    // sandboxed srcDoc. The downloaded page has a second page to open, so the same
    // three calls to action become real links. Same classes either way, so the CSS
    // does not have to know which mode it is in.
    const cta = (extraClass = "") => {
        const cls = `cta${extraClass ? " " + extraClass : ""}`;
        return inline
            ? `<button type="button" class="${cls}">${l.ctaPrimary}</button>`
            : `<a class="${cls}" href="app.html">${l.ctaPrimary}</a>`;
    };

    const styleTag = inline
        ? `<style>\n${starterAppCss(accent)}\n</style>`
        : `<link rel="stylesheet" href="styles.css" />`;

    // Hero copy: the user's tagline wins the headline; their "what it does" wins
    // the blurb. Both fall back to generic template lines.
    const headline = rawTagline ? tagline : l.heroTitle;
    const blurb = whatItDoes || l.heroBlurb;
    const builtForLine = whoItsFor
        ? `<p class="built-for">${fillToken(esc(labels.builtFor), "{{WHO}}", whoItsFor)}</p>`
        : "";

    // The "About"-style band only appears when they named a standout feature.
    const highlightBand = oneFeature
        ? `
  <section class="band">
    <div class="wrap band-inner">
      <div class="band-media">
        ${placeholder("ph-sq")}
        ${placeholder("ph-sq ph-sq--offset")}
      </div>
      <div class="band-text">
        <span class="eyebrow">${l.highlightLabel}</span>
        <h3>${oneFeature}</h3>
        ${cta("cta--ghost")}
      </div>
    </div>
  </section>`
        : "";

    return `<!doctype html>
<html lang="${lang}">
<head>
<meta charset="utf-8" />
<meta name="viewport" content="width=device-width, initial-scale=1" />
<title>${appName}</title>
${styleTag}
</head>
<body>
  <header class="topbar">
    <div class="brand">
      <span class="logo">${emoji}</span>
      <span class="name">${appName}</span>
    </div>
    ${cta()}
  </header>

  <section class="hero">
    <div class="wrap hero-inner">
      <div class="hero-text">
        <span class="eyebrow">${l.eyebrow}</span>
        <h1>${headline}</h1>
        <p class="lead">${blurb}</p>
        ${builtForLine}
        <div>${cta("cta--lg")}</div>
      </div>
      <div class="hero-media">
        ${placeholder()}
      </div>
    </div>
  </section>

  <section class="features" id="features">
    <div class="wrap">
      <h2>${l.insideTitle}</h2>
      <p class="sub">${l.welcomeBody}</p>
      <div class="grid">
        <div class="fcard">
          <div class="tick">&#10003;</div>
          <h3>${l.featAccountsTitle}</h3>
          <p>${l.featAccountsBody}</p>
          <div class="who">
            <span class="avatar">${demoInitial}</span>
            <span>${l.signedInAs} <strong>${l.demoUser}</strong></span>
          </div>
        </div>

        <div class="fcard">
          <div class="tick">&#10003;</div>
          <h3>${l.itemsTitle}</h3>
          <p>${l.itemsHint}</p>
          <ul class="items">
            <li><span class="dot"></span>${l.item1}</li>
            <li><span class="dot"></span>${l.item2}</li>
            <li><span class="dot"></span>${l.item3}</li>
          </ul>
        </div>

        <div class="fcard">
          <div class="tick">&#10003;</div>
          <h3>${l.aiTitle}</h3>
          <p>${l.aiHint}</p>
          <div class="ai-reply">${l.aiReply}</div>
        </div>
      </div>
    </div>
  </section>
${highlightBand}
  <footer>${l.footer} &middot; ${appName}</footer>
</body>
</html>`;
};

export default buildStarterAppHtml;
